import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button as MuiButton,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Drawer,
    IconButton,
    Pagination,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";

import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import AdminUploadDiagnosticsDrawer from "./AdminUploadDiagnosticsDrawer";

import {
    adminTakeOverUpload,
    fetchAdminUploadDetail,
    fetchAdminUploadsByOutcome,
} from "@/Service/AdminUploadMonitorService";

import {
    formatAbsolute,
    formatCalendarDate,
    formatDurationMs,
    formatRelative,
} from "@/utils/adminDates";

import { formatAdminTransferRate } from "@/utils/adminTransferRate";

import {
    getAdminOutcomeConfig,
    getAdminOutcomeHeaderStyles,
    getUploadStatusDisplay,
} from "./adminOutcomeConfig";

// Shared source of truth for both takeover surfaces (live diagnostics drawer + this
// outcome drawer) so the button appears under identical conditions everywhere.
import { ADMIN_TAKEOVER_STATUSES } from "./adminTakeoverStatuses";


const PAGE_SIZE = 20;


const RANGE_LABEL = {
    "24h": "24h",
    "7d": "7 days",
    "30d": "30 days",
    all: "all time",
};


const formatBytes = (bytes) => {
    if (bytes == null || bytes <= 0) {
        return "0 B";
    }

    if (bytes < 1024) {
        return `${bytes} B`;
    }

    const kb = bytes / 1024;

    if (kb < 1024) {
        return `${kb.toFixed(1)} KB`;
    }

    const mb = kb / 1024;

    if (mb < 1024) {
        return `${mb.toFixed(1)} MB`;
    }

    return `${(mb / 1024).toFixed(2)} GB`;
};


// Short-form session id for display — the raw browser tab UUID is 36 chars which clutters the
// timeline. First 8 chars is unique enough within any single upload's history.
const shortSessionId = (sessionId) =>
    typeof sessionId === "string" && sessionId.length > 8
        ? `${sessionId.slice(0, 8)}…`
        : sessionId || "—";

// Human-friendly pause reason mapping — matches PauseReason enum values on the server.
const PAUSE_REASON_LABEL = {
    USER: "User clicked pause",
    STALL: "Sweeper auto-pause (15 min inactivity)",
    WATCHDOG: "Client watchdog abort",
    UNKNOWN: "Unknown (legacy row)",
};

// Client watchdog event source → display label. Server-side StallTelemetryService uses these
// enum-like strings; the mapping mirrors the "source" field it accepts.
const STALL_SOURCE_LABEL = {
    abort: "Auto abort+restart",
    manual: "User clicked Retry now",
    "give-up": "Watchdog exhausted budget",
};

const formatPackageNumber = (directoryName) => {
    if (!directoryName) {
        return "—";
    }

    const dash = directoryName.indexOf("-");

    return dash > 0
        ? directoryName.slice(0, dash)
        : directoryName;
};


const formatDeviceLabel = (detail) => {
    if (!detail?.clientDeviceType) {
        return null;
    }

    const category =
        `${detail.clientDeviceType.charAt(0).toUpperCase()}${detail.clientDeviceType.slice(1)}`;

    if (
        detail.clientDeviceModel &&
        detail.clientDeviceModel.toLowerCase() !==
        detail.clientDeviceType.toLowerCase()
    ) {
        return [category, detail.clientDeviceModel].join(" · ");
    }

    return category;
};


const formatBrowserLabel = (detail) => {
    if (!detail?.clientBrowserName) {
        return null;
    }

    return detail.clientBrowserVersion
        ? `${detail.clientBrowserName} ${detail.clientBrowserVersion}`
        : detail.clientBrowserName;
};


const hasDisplayValue = (value) => {
    if (value == null) {
        return false;
    }

    if (Array.isArray(value)) {
        return value.some((entry) =>
            typeof entry === "string"
                ? entry.trim()
                : entry != null
        );
    }

    if (typeof value === "string") {
        return value.trim().length > 0;
    }

    return true;
};


const buildPackageDetailRows = (packageDetails) => {
    if (!packageDetails) {
        return [];
    }

    const rows = [
        {
            label: "Description",
            value: packageDetails.description,
            multiline: true,
        },
        {
            label: "Country",
            value: packageDetails.country,
            wrap: true,
        },
        {
            label: "State",
            value: packageDetails.state,
            wrap: true,
        },
        {
            label: "City",
            value: packageDetails.city,
            wrap: true,
        },
        {
            label: "Shot date",
            value: packageDetails.shotDate
                ? formatCalendarDate(packageDetails.shotDate)
                : null,
        },
        {
            label: "Notes",
            value: packageDetails.notes,
            multiline: true,
        },
        {
            label: "Uploaded for",
            value: packageDetails.uploadedFor,
            wrap: true,
        },
        {
            label: "Embargo date",
            value: packageDetails.embargoDate
                ? formatCalendarDate(packageDetails.embargoDate)
                : null,
        },
        {
            label: "Assignment editor",
            value: Array.isArray(packageDetails.assignmentEditors)
                ? packageDetails.assignmentEditors
                    .filter(
                        (email) =>
                            typeof email === "string" &&
                            email.trim()
                    )
                    .join("\n")
                : null,
            multiline: true,
        },
    ];

    return rows.filter((row) =>
        hasDisplayValue(row.value)
    );
};


const outcomeTimestampLabel = (detail) => {
    if (detail?.cancelledAt) {
        return "Cancelled";
    }

    if (detail?.failedAt) {
        return "Failed";
    }

    if (detail?.completedAt) {
        return "Completed";
    }

    return "Last update";
};


const outcomeTimestampValue = (detail) =>
    detail?.cancelledAt ||
    detail?.failedAt ||
    detail?.completedAt ||
    detail?.updatedAt;


const CHUNK_STATUS_COLORS = {
    PENDING: "rgba(148,163,184,0.18)",
    UPLOADING: "rgba(96,165,250,0.20)",
    UPLOADED: "rgba(74,222,128,0.18)",
    FAILED: "rgba(248,113,113,0.20)",
};


const CHUNK_STATUS_FG = {
    PENDING: "#cbd5e1",
    UPLOADING: "#93c5fd",
    UPLOADED: "#86efac",
    FAILED: "#fca5a5",
};


const AdminOutcomeDrawer = ({
                                open,
                                outcome,
                                range,
                                onClose,
                            }) => {
    const [page, setPage] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    // Upload Observability Platform: id of the row whose Diagnostics drawer is
    // open on top of this outcome drawer. Kept at the outcome-drawer level so
    // both the row-header bug icon and the "Open diagnostics" button inside
    // the expanded detail share one drawer instance.
    const [diagnosticsUploadId, setDiagnosticsUploadId] = useState(null);


    useEffect(() => {
        if (open) {
            setPage(0);
            setData(null);
            setError(null);
            setExpandedId(null);
            setDiagnosticsUploadId(null);
        }
    }, [open, outcome, range]);


    useEffect(() => {
        if (!open || !outcome) {
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const res = await fetchAdminUploadsByOutcome({
                    outcome,
                    range,
                    page,
                    size: PAGE_SIZE,
                });

                if (!cancelled) {
                    setData(res);
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(
                        e?.response?.data?.message ||
                        e?.message ||
                        "Failed to load uploads"
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [open, outcome, range, page]);


    const handleToggleExpand = useCallback((uploadId) => {
        setExpandedId((prev) =>
            prev === uploadId ? null : uploadId
        );
    }, []);


    const items = data?.items ?? [];
    const totalElements = data?.totalElements ?? 0;
    const totalPages = data?.totalPages ?? 0;


    const title = useMemo(() => {
        const label =
            getAdminOutcomeConfig(outcome)?.label ||
            outcome ||
            "Uploads";

        const rangeText =
            RANGE_LABEL[range] ||
            range ||
            "";

        return rangeText
            ? `${label} · ${rangeText}`
            : label;
    }, [outcome, range]);


    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: {
                        xs: "100%",
                        sm: 460,
                        md: 520,
                    },
                    background: "rgba(17,28,44,0.98)",
                    color: "#f7fafa",
                    borderLeft:
                        "1px solid rgba(255,255,255,0.08)",
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom:
                        "1px solid rgba(255,255,255,0.08)",
                    background:
                        "rgba(41,56,77,0.55)",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: 15,
                            lineHeight: 1.2,
                            color: "#f7fafa",
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        sx={{
                            color: "rgba(247,250,250,0.68)",
                            fontSize: 11,
                            mt: 0.25,
                        }}
                    >
                        {loading
                            ? "Loading…"
                            : `${totalElements.toLocaleString()} total · attributed to upload start time`}
                    </Typography>
                </Box>

                <IconButton
                    onClick={onClose}
                    sx={{ color: "#f7fafa" }}
                    size="small"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Stack>

            <Box
                sx={{
                    p: 2,
                    overflowY: "auto",
                    flex: 1,
                }}
            >
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                    >
                        {error}
                    </Alert>
                )}

                {loading && !data && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 6,
                        }}
                    >
                        <CircularProgress
                            size={26}
                            sx={{ color: "#f7fafa" }}
                        />
                    </Box>
                )}

                {!loading &&
                    items.length === 0 &&
                    !error && (
                        <Box
                            sx={{
                                textAlign: "center",
                                color:
                                    "rgba(247,250,250,0.55)",
                                fontSize: 13,
                                py: 6,
                            }}
                        >
                            No uploads in this bucket for the
                            selected period.
                        </Box>
                    )}

                <Stack spacing={1.25}>
                    {items.map((item) => (
                        <OutcomeRow
                            key={item.uploadId}
                            item={item}
                            expanded={
                                expandedId === item.uploadId
                            }
                            onToggle={() =>
                                handleToggleExpand(
                                    item.uploadId
                                )
                            }
                            onOpenDiagnostics={setDiagnosticsUploadId}
                        />
                    ))}
                </Stack>

                {totalPages > 1 && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 2,
                        }}
                    >
                        <Pagination
                            size="small"
                            count={totalPages}
                            page={page + 1}
                            onChange={(_, value) =>
                                setPage(value - 1)
                            }
                            sx={{
                                "& .MuiPaginationItem-root": {
                                    color: "#f7fafa",
                                    borderColor:
                                        "rgba(255,255,255,0.12)",
                                },
                                "& .Mui-selected": {
                                    background:
                                        "rgba(93,98,182,0.35) !important",
                                    color:
                                        "#fff !important",
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>

            <AdminUploadDiagnosticsDrawer
                open={!!diagnosticsUploadId}
                uploadId={diagnosticsUploadId}
                initialRow={items.find(
                    (r) => r.uploadId === diagnosticsUploadId
                )}
                onClose={() => setDiagnosticsUploadId(null)}
            />
        </Drawer>
    );
};


const OutcomeRow = ({
                        item,
                        expanded,
                        onToggle,
                        onOpenDiagnostics,
                    }) => {
    const tone = getAdminOutcomeHeaderStyles(
        item.status,
        item.cancelReason
    );

    return (
        <Box
            sx={{
                border:
                    "1px solid rgba(255,255,255,0.09)",
                borderRadius: "10px",
                background:
                    "rgba(255,255,255,0.035)",
                overflow: "hidden",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{
                    p: 1.25,
                    cursor: "pointer",
                    background: tone.background,
                    borderBottom: expanded
                        ? `1px solid ${tone.border}`
                        : "1px solid transparent",
                    transition:
                        "background 0.15s ease, border-color 0.15s ease",
                    "&:hover": {
                        background:
                        tone.backgroundHover,
                    },
                }}
                onClick={onToggle}
                role="button"
                aria-expanded={expanded}
                aria-label={`Toggle details for package ${formatPackageNumber(
                    item.directoryName
                )}`}
                data-testid={`outcome-header-${item.uploadId}`}
                data-outcome-key={tone.outcome}
            >
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: tone.dot,
                        flexShrink: 0,
                    }}
                />

                <Box
                    sx={{
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{ minWidth: 0 }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: 13,
                                color: tone.text,
                                flexShrink: 0,
                            }}
                        >
                            #{formatPackageNumber(
                            item.directoryName
                        )}
                        </Typography>

                        <Typography
                            sx={{
                                color:
                                tone.secondaryText,
                                fontSize: 12,
                                overflow: "hidden",
                                textOverflow:
                                    "ellipsis",
                                whiteSpace: "nowrap",
                                minWidth: 0,
                            }}
                        >
                            {item.contributorName ||
                                item.contributorId ||
                                "—"}
                        </Typography>
                    </Stack>

                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                            mt: 0.5,
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            sx={{
                                color:
                                tone.secondaryText,
                                fontSize: 11.5,
                            }}
                        >
                            {formatBytes(item.totalSize)}
                        </Typography>

                        <Box
                            sx={{
                                color:
                                    "rgba(247,250,250,0.45)",
                                fontSize: 11,
                            }}
                        >
                            ·
                        </Box>

                        <Typography
                            sx={{
                                color:
                                tone.secondaryText,
                                fontSize: 11.5,
                            }}
                        >
                            {item.expectedFilesCount ?? "—"}{" "}
                            file
                            {item.expectedFilesCount === 1
                                ? ""
                                : "s"}
                        </Typography>

                        <Box
                            sx={{
                                color:
                                    "rgba(247,250,250,0.45)",
                                fontSize: 11,
                            }}
                        >
                            ·
                        </Box>

                        <Typography
                            sx={{
                                color:
                                tone.secondaryText,
                                fontSize: 11.5,
                            }}
                        >
                            {formatRelative(
                                item.cancelledAt ||
                                item.updatedAt ||
                                item.createdAt
                            )}
                        </Typography>
                    </Stack>
                </Box>

                <Tooltip title="Open diagnostics">
                    <IconButton
                        size="small"
                        sx={{ color: tone.text }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenDiagnostics?.(item.uploadId);
                        }}
                        data-testid={`outcome-diagnostics-open-${item.uploadId}`}
                        aria-label={`open diagnostics for ${item.uploadId}`}
                    >
                        <BugReportOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <IconButton
                    size="small"
                    sx={{ color: tone.text }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle();
                    }}
                >
                    {expanded ? (
                        <ExpandLessIcon fontSize="small" />
                    ) : (
                        <ExpandMoreIcon fontSize="small" />
                    )}
                </IconButton>
            </Stack>

            <Collapse
                in={expanded}
                timeout="auto"
                unmountOnExit
            >
                {expanded && (
                    <OutcomeRowDetail
                        uploadId={item.uploadId}
                        onOpenDiagnostics={onOpenDiagnostics}
                    />
                )}
            </Collapse>
        </Box>
    );
};


const OutcomeRowDetail = ({ uploadId, onOpenDiagnostics }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Files accordion is closed by default.
    const [filesOpen, setFilesOpen] = useState(false);

    // Status history likewise — it's a triage aid, not something an admin needs on every expand.
    const [historyOpen, setHistoryOpen] = useState(false);

    // Admin take-over dialog + inline result banner. Lives at the detail level rather than
    // higher up because it's tightly coupled to `detail` (needs status, sessionId, etc.) and
    // the post-success refresh must happen against the same reload closure we already use for
    // the initial fetch.
    const [takeOverOpen, setTakeOverOpen] = useState(false);
    const [takeOverBusy, setTakeOverBusy] = useState(false);
    const [takeOverError, setTakeOverError] = useState(null);
    const [takeOverBanner, setTakeOverBanner] = useState(null);

    const reload = useCallback(async () => {
        try {
            const res = await fetchAdminUploadDetail(uploadId);
            setDetail(res);
            setError(null);
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                e?.message ||
                "Failed to load detail"
            );
        }
    }, [uploadId]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const res =
                    await fetchAdminUploadDetail(
                        uploadId
                    );

                if (!cancelled) {
                    setDetail(res);
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(
                        e?.response?.data?.message ||
                        e?.message ||
                        "Failed to load detail"
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [uploadId]);

    const handleTakeOverConfirm = useCallback(async () => {
        setTakeOverBusy(true);
        setTakeOverError(null);
        try {
            const res = await adminTakeOverUpload(uploadId);
            setTakeOverOpen(false);
            setTakeOverBanner({
                severity: "success",
                message:
                    "You now own this upload. The contributor's session will be evicted on their next poll (~2 s)." +
                    (res?.previousOwnerSessionId
                        ? ` Previous owner session: ${res.previousOwnerSessionId.slice(0, 8)}…`
                        : ""),
            });
            // Refresh so the drawer shows the admin's session id as current and the ownership
            // timeline picks up the new entry.
            await reload();
        } catch (e) {
            setTakeOverError(
                e?.response?.data?.message ||
                e?.message ||
                "Take-over failed"
            );
        } finally {
            setTakeOverBusy(false);
        }
    }, [uploadId, reload]);


    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    py: 2.5,
                    color:
                        "rgba(247,250,250,0.7)",
                }}
            >
                <CircularProgress
                    size={18}
                    sx={{ color: "#f7fafa" }}
                />

                <Typography
                    sx={{
                        fontSize: 12,
                        color:
                            "rgba(247,250,250,0.7)",
                    }}
                >
                    Loading details…
                </Typography>
            </Box>
        );
    }


    if (error) {
        return (
            <Box sx={{ p: 1.5 }}>
                <Alert severity="error">
                    {error}
                </Alert>
            </Box>
        );
    }


    if (!detail) {
        return null;
    }


    const deviceLabel =
        formatDeviceLabel(detail);

    const browserLabel =
        formatBrowserLabel(detail);

    const averageSpeed =
        formatAdminTransferRate(
            detail.averageUploadSpeedBytesPerSecond
        );

    // Median across per-PATCH samples — decoupled from wall-time idle periods that skew the
    // average. See UploadThroughputSamplesService on the server for the reservoir.
    const medianSpeed =
        formatAdminTransferRate(
            detail.medianUploadSpeedBytesPerSecond
        );

    const packageDetailRows =
        buildPackageDetailRows(
            detail.packageDetails
        );

    const showPackageDetails =
        packageDetailRows.length > 0;

    const showFailureBlock =
        detail.status === "FAILED" &&
        (
            detail.failureMessage ||
            detail.failureStackTraceExcerpt
        );


    return (
        <Box
            sx={{
                p: 1.5,
                background:
                    "rgba(11,18,32,0.55)",
            }}
            data-testid={`outcome-body-${uploadId}`}
        >
            <Stack
                spacing={0.75}
                sx={{ mb: 1.5 }}
            >
                <DetailLine
                    label="Package name"
                    value={
                        detail.packageName || "—"
                    }
                    wrap
                />

                <DetailLine
                    label="Status"
                    value={detail.status || "—"}
                    wrap
                />

                {detail.cancelReason && (
                    <DetailLine
                        label="Cancel reason"
                        value={detail.cancelReason}
                        wrap
                    />
                )}

                {detail.cancelNotes && (
                    <DetailLine
                        label="Cancel notes"
                        value={detail.cancelNotes}
                        multiline
                    />
                )}

                {deviceLabel && (
                    <DetailLine
                        label="Device"
                        value={deviceLabel}
                        wrap
                    />
                )}

                {browserLabel && (
                    <DetailLine
                        label="Browser"
                        value={browserLabel}
                        wrap
                    />
                )}

                {averageSpeed && (
                    <DetailLine
                        label="Avg. speed"
                        value={averageSpeed}
                    />
                )}

                {medianSpeed && (
                    <DetailLine
                        label="Median speed"
                        value={medianSpeed}
                    />
                )}

                {detail.pauseReason && (
                    <DetailLine
                        label="Pause reason"
                        value={
                            PAUSE_REASON_LABEL[
                                detail.pauseReason
                            ] || detail.pauseReason
                        }
                        wrap
                    />
                )}

                {detail.sessionId && (
                    <DetailLine
                        label="Current session"
                        value={detail.sessionId}
                        wrap
                    />
                )}

                {detail.countryCode && (
                    <DetailLine
                        label="Country code"
                        value={detail.countryCode}
                    />
                )}

                {detail.affiliate && (
                    <DetailLine
                        label="Affiliate"
                        value={detail.affiliate}
                        wrap
                    />
                )}

                {detail.userRating != null && (
                    <DetailLine
                        label="User rating"
                        value={`${detail.userRating} / 5`}
                    />
                )}

                {detail.userFeedback && (
                    <DetailLine
                        label="User feedback"
                        value={detail.userFeedback}
                        multiline
                    />
                )}

                {detail.isFilesCleaned != null && (
                    <DetailLine
                        label="TUS files cleaned"
                        value={
                            detail.isFilesCleaned
                                ? "Yes"
                                : "No"
                        }
                    />
                )}

                {detail.clientUserAgent && (
                    <DetailLine
                        label="User-agent (raw)"
                        value={detail.clientUserAgent}
                        multiline
                    />
                )}

                {detail.retryCount != null &&
                    detail.retryCount > 0 && (
                        <DetailLine
                            label="Retries"
                            value={String(
                                detail.retryCount
                            )}
                        />
                    )}

                <DetailLine
                    label="Bytes"
                    value={`${formatBytes(
                        detail.bytesUploaded
                    )} / ${formatBytes(
                        detail.totalSize
                    )}`}
                    wrap
                />

                <DetailLine
                    label="Files"
                    value={`${
                        detail.filesUploadedCount ?? 0
                    } / ${
                        detail.expectedFilesCount ?? 0
                    } uploaded`}
                />

                <DetailLine
                    label="Started"
                    value={formatAbsolute(
                        detail.startUploadTime
                    )}
                    wrap
                />

                <DetailLine
                    label={outcomeTimestampLabel(
                        detail
                    )}
                    value={formatAbsolute(
                        outcomeTimestampValue(detail)
                    )}
                    wrap
                />

                {detail.uploadTimeServer != null && (
                    <DetailLine
                        label="Server transfer"
                        value={formatDurationMs(
                            detail.uploadTimeServer
                        )}
                    />
                )}

                {detail.uploadTimeProvider != null && (
                    <DetailLine
                        label="Provider transfer"
                        value={formatDurationMs(
                            detail.uploadTimeProvider
                        )}
                    />
                )}
            </Stack>

            {/*
              Take-over action. Only offered while the upload is in a status where the server's
              claim endpoint will accept the transfer (mirrors UploadStatus.ACTIVE_STATUSES).
              For terminal rows (COMPLETED / CANCELLED / FAILED) the button is hidden entirely
              — the contributor's session is already gone so there's nothing to take over.
              The result banner persists after success so the admin gets confirmation even
              after the auto-reload updates the surrounding fields.

              Diagnostics button is offered for ALL statuses, terminal included — that's the
              main way admins jump from a "why did this cancel?" outcome row to the PostHog
              session replay / captured client snapshot.
            */}
            <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 1.25, flexWrap: "wrap", gap: 1 }}
            >
                <MuiButton
                    variant="outlined"
                    size="small"
                    startIcon={<BugReportOutlinedIcon fontSize="small" />}
                    onClick={() => onOpenDiagnostics?.(uploadId)}
                    data-testid={`outcome-detail-diagnostics-${uploadId}`}
                    sx={{
                        color: "#f7fafa",
                        borderColor: "rgba(247,250,250,0.4)",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 11.5,
                        py: 0.4,
                        px: 1.25,
                        "&:hover": {
                            borderColor: "rgba(247,250,250,0.7)",
                            background: "rgba(255,255,255,0.04)",
                        },
                    }}
                >
                    Open diagnostics
                </MuiButton>

                {ADMIN_TAKEOVER_STATUSES.has(detail.status) && (
                    <MuiButton
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            setTakeOverError(null);
                            setTakeOverOpen(true);
                        }}
                        data-testid={`admin-take-over-button-${uploadId}`}
                        sx={{
                            color: "#f7fafa",
                            borderColor: "rgba(247,250,250,0.4)",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: 11.5,
                            py: 0.4,
                            px: 1.25,
                            "&:hover": {
                                borderColor: "rgba(247,250,250,0.7)",
                                background: "rgba(255,255,255,0.04)",
                            },
                        }}
                    >
                        Take over upload
                    </MuiButton>
                )}
            </Stack>

            {takeOverBanner && (
                <Alert
                    severity={takeOverBanner.severity}
                    sx={{ mb: 1.25, fontSize: 12 }}
                    onClose={() => setTakeOverBanner(null)}
                    data-testid={`admin-take-over-banner-${uploadId}`}
                >
                    {takeOverBanner.message}
                </Alert>
            )}

            {showPackageDetails && (
                <PackageDetailsSection
                    uploadId={uploadId}
                    rows={packageDetailRows}
                />
            )}

            {detail.stallSnapshot && (
                <StallSnapshotSection
                    uploadId={uploadId}
                    snapshot={detail.stallSnapshot}
                />
            )}

            {Array.isArray(detail.sessionHistory) &&
                detail.sessionHistory.length > 1 && (
                <SessionHistorySection
                    uploadId={uploadId}
                    entries={detail.sessionHistory}
                />
            )}

            {showFailureBlock && (
                <>
                    <Divider
                        sx={{
                            borderColor:
                                "rgba(255,255,255,0.08)",
                            my: 1.25,
                        }}
                    />

                    {detail.failureMessage && (
                        <DetailLine
                            label="Error"
                            value={
                                detail.failureMessage
                            }
                            multiline
                        />
                    )}

                    {detail.failureStackTraceExcerpt && (
                        <Stack
                            spacing={0.5}
                            sx={{
                                mt: 0.85,
                                mb: 1.5,
                            }}
                        >
                            <Typography
                                sx={{
                                    color:
                                        "rgba(247,250,250,0.5)",
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    letterSpacing:
                                        "0.04em",
                                    textTransform:
                                        "uppercase",
                                }}
                            >
                                Stacktrace
                            </Typography>

                            <Box
                                component="pre"
                                sx={{
                                    m: 0,
                                    px: 1,
                                    py: 0.9,
                                    borderRadius: "8px",
                                    background:
                                        "rgba(0,0,0,0.22)",
                                    color: "#f7fafa",
                                    fontFamily:
                                        "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                    fontSize: 11.5,
                                    lineHeight: 1.45,
                                    whiteSpace:
                                        "pre-wrap",
                                    wordBreak:
                                        "break-word",
                                    maxHeight: 180,
                                    overflowY: "auto",
                                }}
                                data-testid={`failure-stacktrace-${uploadId}`}
                            >
                                {
                                    detail.failureStackTraceExcerpt
                                }
                            </Box>
                        </Stack>
                    )}
                </>
            )}


            <FilesSection
                uploadId={uploadId}
                files={detail.files || []}
                expanded={filesOpen}
                onToggle={() =>
                    setFilesOpen((prev) => !prev)
                }
            />

            <StatusHistorySection
                uploadId={uploadId}
                history={detail.statusHistory || []}
                expanded={historyOpen}
                onToggle={() =>
                    setHistoryOpen((prev) => !prev)
                }
            />

            <Dialog
                open={takeOverOpen}
                onClose={() => (takeOverBusy ? null : setTakeOverOpen(false))}
                data-testid={`admin-take-over-dialog-${uploadId}`}
            >
                <DialogTitle sx={{ fontSize: 16 }}>
                    Take over this upload?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 13, mb: 1 }}>
                        Your admin session will become the owner of
                        <strong> {detail.packageName || detail.directoryName || uploadId}</strong>.
                        The contributor's browser will lose control on its next poll (~2 s) and
                        the upload card will disappear from their view.
                    </DialogContentText>
                    <DialogContentText sx={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
                        Note: this transfers <em>ownership</em>, not the source files. Once you
                        own the upload you can cancel it cleanly or let the server continue
                        finalisation. To actually resume uploading you would need the same files
                        on your machine.
                    </DialogContentText>
                    {takeOverError && (
                        <Alert severity="error" sx={{ mt: 1.25, fontSize: 12 }}>
                            {takeOverError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <MuiButton
                        onClick={() => setTakeOverOpen(false)}
                        disabled={takeOverBusy}
                        size="small"
                    >
                        Cancel
                    </MuiButton>
                    <MuiButton
                        onClick={handleTakeOverConfirm}
                        disabled={takeOverBusy}
                        variant="contained"
                        color="warning"
                        size="small"
                        data-testid={`admin-take-over-confirm-${uploadId}`}
                    >
                        {takeOverBusy ? "Taking over…" : "Take over"}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};


const PackageDetailsSection = ({
                                   uploadId,
                                   rows,
                               }) => (
    <Box
        sx={{
            mt: 1.25,
            mb: 1.25,
            border:
                "1px solid rgba(255,255,255,0.08)",
            borderRadius: "8px",
            overflow: "hidden",
            background:
                "rgba(255,255,255,0.025)",
        }}
        data-testid={`package-details-section-${uploadId}`}
    >
        <Box
            sx={{
                px: 1.25,
                py: 0.95,
                background:
                    "rgba(255,255,255,0.035)",
                borderBottom:
                    "1px solid rgba(255,255,255,0.08)",
            }}
        >
            <Typography
                sx={{
                    color:
                        "rgba(247,250,250,0.78)",
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                }}
            >
                Package Details
            </Typography>
        </Box>

        <Stack
            spacing={0.75}
            sx={{
                px: 1.25,
                py: 1,
                background:
                    "rgba(11,18,32,0.25)",
            }}
            data-testid={`package-details-content-${uploadId}`}
        >
            {rows.map((row) => (
                <DetailLine
                    key={row.label}
                    label={row.label}
                    value={row.value}
                    multiline={row.multiline}
                    wrap={row.wrap}
                />
            ))}
        </Stack>
    </Box>
);


const FilesSection = ({
                          uploadId,
                          files,
                          expanded,
                          onToggle,
                      }) => (
    <Box
        sx={{
            mt: 1.25,
            border:
                "1px solid rgba(255,255,255,0.08)",
            borderRadius: "8px",
            overflow: "hidden",
            background:
                "rgba(255,255,255,0.025)",
        }}
        data-testid={`files-section-${uploadId}`}
    >
        <Box
            onClick={onToggle}
            role="button"
            aria-expanded={expanded}
            data-testid={`files-toggle-${uploadId}`}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.95,
                cursor: "pointer",
                userSelect: "none",
                background:
                    "rgba(255,255,255,0.035)",
                transition:
                    "background 0.2s ease",
                "&:hover": {
                    background:
                        "rgba(255,255,255,0.055)",
                },
            }}
        >
            <Typography
                sx={{
                    color:
                        "rgba(247,250,250,0.78)",
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                }}
            >
                Files
            </Typography>

            <Typography
                sx={{
                    color:
                        "rgba(247,250,250,0.48)",
                    fontSize: 10.5,
                    fontWeight: 700,
                }}
            >
                ({files.length})
            </Typography>

            <Box sx={{ flex: 1 }} />

            <ExpandMoreIcon
                sx={{
                    color:
                        "rgba(247,250,250,0.7)",
                    fontSize: 18,
                    transform: expanded
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    transition:
                        "transform 0.2s ease",
                }}
            />
        </Box>

        <Collapse
            in={expanded}
            timeout={200}
            unmountOnExit
        >
            <Stack
                spacing={0.5}
                sx={{
                    p: 1,
                    background:
                        "rgba(11,18,32,0.25)",
                    borderTop:
                        "1px solid rgba(255,255,255,0.08)",
                }}
                data-testid={`files-content-${uploadId}`}
            >
                {files.map((file) => (
                    <FileRow
                        key={file.id}
                        file={file}
                    />
                ))}

                {files.length === 0 && (
                    <Typography
                        sx={{
                            color:
                                "rgba(247,250,250,0.5)",
                            fontSize: 12,
                            px: 0.5,
                            py: 0.25,
                        }}
                    >
                        No files recorded for this package.
                    </Typography>
                )}
            </Stack>
        </Collapse>
    </Box>
);


// The status-transition audit trail (upload_metadata_status_history), oldest first. Answers the
// question the outcome fields can't: not just "this failed" but how it got there — how long it
// spent uploading, whether it bounced through PAUSED, when the sweeper stepped in. The rows are
// written by a Postgres trigger and carry only (status, changedAt); the "why" lives in the
// cancel reason / failure message rendered above, so there is deliberately no actor column here.
const StatusHistorySection = ({
                                 uploadId,
                                 history,
                                 expanded,
                                 onToggle,
                             }) => (
    <Box
        sx={{
            mt: 1.25,
            border:
                "1px solid rgba(255,255,255,0.08)",
            borderRadius: "8px",
            overflow: "hidden",
            background:
                "rgba(255,255,255,0.025)",
        }}
        data-testid={`status-history-section-${uploadId}`}
    >
        <Box
            onClick={onToggle}
            role="button"
            aria-expanded={expanded}
            data-testid={`status-history-toggle-${uploadId}`}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.95,
                cursor: "pointer",
                userSelect: "none",
                background:
                    "rgba(255,255,255,0.035)",
                transition:
                    "background 0.2s ease",
                "&:hover": {
                    background:
                        "rgba(255,255,255,0.055)",
                },
            }}
        >
            <Typography
                sx={{
                    color:
                        "rgba(247,250,250,0.78)",
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                }}
            >
                Status history
            </Typography>

            <Typography
                sx={{
                    color:
                        "rgba(247,250,250,0.48)",
                    fontSize: 10.5,
                    fontWeight: 700,
                }}
            >
                ({history.length})
            </Typography>

            <Box sx={{ flex: 1 }} />

            <ExpandMoreIcon
                sx={{
                    color:
                        "rgba(247,250,250,0.7)",
                    fontSize: 18,
                    transform: expanded
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    transition:
                        "transform 0.2s ease",
                }}
            />
        </Box>

        <Collapse
            in={expanded}
            timeout={200}
            unmountOnExit
        >
            <Box
                sx={{
                    p: 1,
                    background:
                        "rgba(11,18,32,0.25)",
                    borderTop:
                        "1px solid rgba(255,255,255,0.08)",
                }}
                data-testid={`status-history-content-${uploadId}`}
            >
                {history.map((entry, index) => (
                    <StatusHistoryRow
                        key={`${entry.status}-${entry.changedAt}-${index}`}
                        entry={entry}
                        isLast={
                            index === history.length - 1
                        }
                    />
                ))}

                {history.length === 0 && (
                    <Typography
                        sx={{
                            color:
                                "rgba(247,250,250,0.5)",
                            fontSize: 12,
                            px: 0.5,
                            py: 0.25,
                        }}
                    >
                        No status history recorded for this package.
                    </Typography>
                )}
            </Box>
        </Collapse>
    </Box>
);


const StatusHistoryRow = ({ entry, isLast }) => {
    const display = getUploadStatusDisplay(
        entry.status
    );

    // Every admin timestamp goes through the shared adminDates helpers — the backend serialises
    // Instants in shapes a bare `new Date()` silently turns into 1970. Both helpers return an
    // em-dash for a missing value, so gate on the raw field rather than on their output.
    const hasChangedAt = entry.changedAt != null;

    const changedAtText = hasChangedAt
        ? `${formatAbsolute(entry.changedAt)} · ${formatRelative(entry.changedAt)}`
        : "—";

    return (
        <Stack
            direction="row"
            spacing={1.25}
            sx={{ minWidth: 0 }}
        >
            {/* Rail: a dot per transition, joined by a connector that stops at the last one. */}
            <Stack
                alignItems="center"
                sx={{ pt: 0.75 }}
            >
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        flexShrink: 0,
                        background: display.color,
                    }}
                />

                {!isLast && (
                    <Box
                        sx={{
                            width: "1px",
                            flex: 1,
                            minHeight: 22,
                            background:
                                "rgba(255,255,255,0.12)",
                        }}
                    />
                )}
            </Stack>

            <Stack
                spacing={0.25}
                sx={{
                    minWidth: 0,
                    pb: isLast ? 0.25 : 1.25,
                    flex: 1,
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ flexWrap: "wrap" }}
                >
                    <Chip
                        size="small"
                        label={display.label}
                        sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 700,
                            color: display.color,
                            background:
                                display.background,
                            border: `1px solid ${display.border}`,
                        }}
                    />

                    {/* Dwell time: how long it sat in this status before moving on. Absent on the
                        newest entry, which the package is still in — flagged as "current" rather
                        than rendered as a misleading zero. */}
                    <Typography
                        sx={{
                            color:
                                "rgba(247,250,250,0.62)",
                            fontSize: 11.5,
                            fontWeight: 600,
                        }}
                    >
                        {entry.durationMillis != null
                            ? `held ${formatDurationMs(
                                entry.durationMillis
                            )}`
                            : "current"}
                    </Typography>
                </Stack>

                <Typography
                    sx={{
                        color:
                            "rgba(247,250,250,0.5)",
                        fontSize: 11,
                        wordBreak: "break-word",
                    }}
                >
                    {changedAtText}
                </Typography>
            </Stack>
        </Stack>
    );
};


const DetailLine = ({
                        label,
                        value,
                        multiline = false,
                        wrap = false,
                    }) => (
    <Stack
        direction={
            multiline ? "column" : "row"
        }
        alignItems={
            multiline || wrap
                ? "flex-start"
                : "baseline"
        }
        spacing={0.5}
        sx={{ minWidth: 0 }}
    >
        <Typography
            sx={{
                color:
                    "rgba(247,250,250,0.5)",
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                flexShrink: 0,
                minWidth: multiline ? 0 : 110,
            }}
        >
            {label}
        </Typography>

        <Typography
            sx={{
                color: "#f7fafa",
                fontSize: 12.5,

                wordBreak:
                    multiline || wrap
                        ? "break-word"
                        : "normal",

                overflowWrap:
                    multiline || wrap
                        ? "anywhere"
                        : "normal",

                whiteSpace:
                    multiline
                        ? "pre-wrap"
                        : wrap
                            ? "normal"
                            : "nowrap",

                overflow:
                    multiline || wrap
                        ? "visible"
                        : "hidden",

                textOverflow:
                    multiline || wrap
                        ? "clip"
                        : "ellipsis",

                minWidth: 0,
            }}
        >
            {value}
        </Typography>
    </Stack>
);


// Client-side stall-watchdog telemetry rolled up into a compact panel. Only rendered when
// StallTelemetryService recorded at least one event for this uploadId. Header summarises
// aggregate counts; expanding reveals the full event timeline for triage. The whole panel is
// null for the (common) stall-free case, so the drawer stays uncluttered when nothing needs
// investigating.
const StallSnapshotSection = ({ uploadId, snapshot }) => {
    const [expanded, setExpanded] = useState(false);
    const events = Array.isArray(snapshot?.events)
        ? snapshot.events
        : [];

    return (
        <Box
            sx={{
                mt: 1.25,
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "8px",
                overflow: "hidden",
                background: "rgba(255,255,255,0.025)",
            }}
            data-testid={`stall-snapshot-section-${uploadId}`}
        >
            <Box
                onClick={() => setExpanded((v) => !v)}
                role="button"
                aria-expanded={expanded}
                data-testid={`stall-snapshot-toggle-${uploadId}`}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 0.95,
                    cursor: "pointer",
                    userSelect: "none",
                    background: "rgba(255,255,255,0.035)",
                    transition: "background 0.2s ease",
                    "&:hover": {
                        background: "rgba(255,255,255,0.055)",
                    },
                }}
            >
                <Typography
                    sx={{
                        color: "rgba(247,250,250,0.78)",
                        fontSize: 10.5,
                        fontWeight: 800,
                        letterSpacing: "0.045em",
                        textTransform: "uppercase",
                    }}
                >
                    Client watchdog telemetry
                </Typography>
                <Typography
                    sx={{
                        color: "rgba(247,250,250,0.48)",
                        fontSize: 10.5,
                        fontWeight: 700,
                    }}
                >
                    ({snapshot.totalEvents} events · {snapshot.autoRestartCount} auto ·{" "}
                    {snapshot.manualRetryCount} manual
                    {snapshot.gaveUp ? " · gave up" : ""})
                </Typography>
                <Box sx={{ flex: 1 }} />
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Box>

            <Collapse in={expanded} unmountOnExit>
                <Stack
                    spacing={0.5}
                    sx={{ px: 1.25, py: 0.9 }}
                    data-testid={`stall-snapshot-content-${uploadId}`}
                >
                    <DetailLine
                        label="Last event"
                        value={`${STALL_SOURCE_LABEL[snapshot.lastEventSource] || snapshot.lastEventSource} — ${formatAbsolute(snapshot.lastEventAt)}`}
                        wrap
                    />
                    <DetailLine
                        label="Stall duration"
                        value={`${Math.round((snapshot.lastStalledMs || 0) / 1000)} s`}
                    />
                    {snapshot.lastRollingBps != null && (
                        <DetailLine
                            label="Rolling speed at stall"
                            value={formatAdminTransferRate(snapshot.lastRollingBps)}
                        />
                    )}
                    {snapshot.lastEffectiveType && (
                        <DetailLine
                            label="Connection type"
                            value={snapshot.lastEffectiveType}
                        />
                    )}
                    {events.length > 0 && (
                        <Box sx={{ mt: 0.75 }}>
                            <Typography
                                sx={{
                                    color: "rgba(247,250,250,0.5)",
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    letterSpacing: "0.04em",
                                    textTransform: "uppercase",
                                    mb: 0.5,
                                }}
                            >
                                Timeline ({events.length})
                            </Typography>
                            <Stack spacing={0.4}>
                                {events.map((ev, idx) => (
                                    <Stack
                                        key={`${ev.recordedAt}-${idx}`}
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                            fontSize: 11.5,
                                            color: "rgba(247,250,250,0.72)",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <span>{formatRelative(ev.recordedAt)}</span>
                                        <span style={{ color: "rgba(247,250,250,0.9)" }}>
                                            {STALL_SOURCE_LABEL[ev.source] || ev.source}
                                        </span>
                                        <span>attempt #{ev.restartCount}</span>
                                        <span>stall={Math.round((ev.stalledMs || 0) / 1000)}s</span>
                                        {ev.sessionId && (
                                            <span title={ev.sessionId}>
                                                sess={shortSessionId(ev.sessionId)}
                                            </span>
                                        )}
                                        {ev.filename && (
                                            <span
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    maxWidth: 200,
                                                }}
                                                title={ev.filename}
                                            >
                                                file={ev.filename}
                                            </span>
                                        )}
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </Collapse>
        </Box>
    );
};


// Ownership timeline built from distinct sessionIds seen in stall events + current owner.
// Rendered only when >1 session touched this upload (i.e. an actual takeover happened);
// single-session uploads are the common case and don't warrant the extra panel. The current
// owner is marked so admins can quickly answer "which tab holds the row right now?"
const SessionHistorySection = ({ uploadId, entries }) => (
    <Box
        sx={{
            mt: 1.25,
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "8px",
            overflow: "hidden",
            background: "rgba(255,255,255,0.025)",
        }}
        data-testid={`session-history-section-${uploadId}`}
    >
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.95,
                background: "rgba(255,255,255,0.035)",
            }}
        >
            <Typography
                sx={{
                    color: "rgba(247,250,250,0.78)",
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                }}
            >
                Session ownership
            </Typography>
            <Typography
                sx={{
                    color: "rgba(247,250,250,0.48)",
                    fontSize: 10.5,
                    fontWeight: 700,
                }}
            >
                ({entries.length} sessions)
            </Typography>
        </Box>
        <Stack spacing={0.4} sx={{ px: 1.25, py: 0.9 }}>
            {entries.map((entry) => (
                <Stack
                    key={entry.sessionId}
                    direction="row"
                    spacing={1}
                    sx={{
                        fontSize: 11.5,
                        color: "rgba(247,250,250,0.72)",
                        alignItems: "center",
                        flexWrap: "wrap",
                    }}
                >
                    <span
                        style={{
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            color: entry.isCurrent ? "#f7fafa" : "rgba(247,250,250,0.7)",
                            fontWeight: entry.isCurrent ? 700 : 400,
                        }}
                        title={entry.sessionId}
                    >
                        {shortSessionId(entry.sessionId)}
                    </span>
                    {entry.isCurrent && (
                        <Chip
                            size="small"
                            label="current"
                            sx={{
                                height: 18,
                                fontSize: 9.5,
                                fontWeight: 700,
                                background: "rgba(56,189,248,0.22)",
                                color: "#7dd3fc",
                            }}
                        />
                    )}
                    {entry.firstSeenAt && (
                        <span>first seen {formatRelative(entry.firstSeenAt)}</span>
                    )}
                    <span>{entry.seenCount} event(s)</span>
                </Stack>
            ))}
        </Stack>
    </Box>
);


const FileRow = ({ file }) => {
    const total =
        file.sizeBytes ||
        file.sizeBytesExpected ||
        0;

    const status =
        file.chunkStatus || "PENDING";

    // Tier 2 additions: lifecycle & TUS metadata. Hidden unless the values are actually
    // present so the row stays compact for the common "no anomaly" case; when an admin does
    // need to trace a specific file (e.g. grep the tusUploadId in server logs) the details
    // are one click away rather than requiring a separate API call.
    const hasLifecycle =
        file.tusUploadId ||
        file.tusUploadUrl ||
        file.storageKey ||
        file.createdAt;

    return (
        <Stack spacing={0.35} sx={{ px: 1, py: 0.65, borderRadius: "8px", background: "rgba(255,255,255,0.025)", minWidth: 0 }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ minWidth: 0 }}
            >
                <Typography
                    sx={{
                        color: "#f7fafa",
                        fontSize: 12.5,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {file.originalName || "—"}
                </Typography>

                <Typography
                    sx={{
                        color:
                            "rgba(247,250,250,0.65)",
                        fontSize: 11.5,
                        flexShrink: 0,
                    }}
                >
                    {formatBytes(file.bytesUploaded)} /{" "}
                    {formatBytes(total)}
                </Typography>

                <Chip
                    size="small"
                    label={status}
                    sx={{
                        background:
                            CHUNK_STATUS_COLORS[
                                status
                                ] ||
                            "rgba(148,163,184,0.18)",

                        color:
                            CHUNK_STATUS_FG[
                                status
                                ] ||
                            "#cbd5e1",

                        fontWeight: 700,
                        fontSize: 10,
                        height: 20,
                        flexShrink: 0,
                    }}
                />
            </Stack>
            {hasLifecycle && (
                <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{
                        color: "rgba(247,250,250,0.4)",
                        fontSize: 10.5,
                        flexWrap: "wrap",
                    }}
                    data-testid={`file-lifecycle-${file.id}`}
                >
                    {file.tusUploadId && (
                        <span title={file.tusUploadUrl || undefined}>
                            tus={file.tusUploadId}
                        </span>
                    )}
                    {file.createdAt && (
                        <span>created={formatAbsolute(file.createdAt)}</span>
                    )}
                    {file.storageKey && (
                        <span
                            title={file.storageKey}
                            style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 260,
                            }}
                        >
                            storage={file.storageKey}
                        </span>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default AdminOutcomeDrawer;